import { useDashboardLeaguesControllerDetail } from "@/api/__generated__/dashboard-leagues/dashboard-leagues";
import { useCurrentLeague } from "@/contexts";
import { mapToLeagueNamingNullable } from "@/mappers";
import { LeagueNaming } from "@/types";

/**
 * Resolve the current league's `tourType` (`"tour"` / `"league"` / `null`).
 *
 * `useCurrentLeague()` is backed by the leagues `refs` endpoint, which does NOT
 * carry `tourType`; only the league DETAIL endpoint does. This hook fetches the
 * detail for the current league (heavily cached — the same call the update-league
 * page makes) purely to read the naming variant, so callers can gate
 * league-only surfaces (e.g. the global filter bar) on `tourType === "league"`.
 *
 * Returns `{ tourType, isLeague, isLoading }`. `tourType` is `null` when no
 * league is selected, while the detail loads, or for non tour/league containers.
 */
export const useCurrentLeagueTourType = (): {
  tourType: LeagueNaming | null;
  isLeague: boolean;
  isLoading: boolean;
} => {
  const league = useCurrentLeague();
  const leagueId = league?.id ?? null;

  const { data, isLoading } = useDashboardLeaguesControllerDetail(leagueId ?? "", {
    query: { enabled: !!leagueId, staleTime: 5 * 60_000 },
  });

  const tourType = data ? mapToLeagueNamingNullable(data.tourType) : null;

  return {
    tourType,
    isLeague: tourType === "league",
    isLoading: !!leagueId && isLoading,
  };
};
